// Login Page

import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuthStore } from '../../stores/auth-store';
import { trpc } from '../../lib/trpc';

interface LoginFormData {
  email: string;
  password: string;
  tenantSlug?: string;
}

interface LocationState {
  message?: string;
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuthStore();
  const [showTenantSelect, setShowTenantSelect] = useState(false);
  const [availableTenants, setAvailableTenants] = useState<{ slug: string; name: string }[]>([]);

  // Get success message from navigation state (e.g., after access request)
  const locationState = location.state as LocationState | null;
  const successMessage = locationState?.message;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    setValue,
  } = useForm<LoginFormData>();

  const loginMutation = trpc.auth.login.useMutation({
    onSuccess: (data) => {
      if (data.requiresMfa) {
        // Redirect to MFA verification
        navigate('/verify-mfa', { state: { email: data.user?.email } });
        return;
      }

      login(data.accessToken, data.refreshToken, data.user, data.expiresAt);
      navigate('/dashboard');
    },
    onError: (error) => {
      const errorData = error.data as { cause?: { tenants?: { slug: string; name: string }[] } } | null | undefined;
      if (errorData?.cause?.tenants) {
        // Multiple tenants - show selector
        setAvailableTenants(errorData.cause.tenants);
        setShowTenantSelect(true);
      } else {
        setError('root', { message: error.message });
      }
    },
  });

  const onSubmit = (data: LoginFormData) => {
    loginMutation.mutate(data);
  };

  const selectTenant = (slug: string) => {
    setValue('tenantSlug', slug);
    setShowTenantSelect(false);
    handleSubmit(onSubmit)();
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <img src="/calculom.png" alt="Calculom" className="w-[110px] h-auto mx-auto mb-4" />
          <p className="text-gray-600">Sign in to your account</p>
        </div>

        <div className="card">
          {showTenantSelect ? (
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Select Organization</h2>
              <p className="text-sm text-gray-600 mb-4">
                You have access to multiple organizations. Please select one:
              </p>
              <div className="space-y-2">
                {availableTenants.map((tenant) => (
                  <button
                    key={tenant.slug}
                    type="button"
                    onClick={() => selectTenant(tenant.slug)}
                    className="w-full rounded-lg border border-gray-200 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-medium">{tenant.name}</span>
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setShowTenantSelect(false)}
                className="mt-4 text-sm text-gray-500 hover:text-gray-700"
              >
                Back to login
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {successMessage && (
                <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-700">
                  {successMessage}
                </div>
              )}

              {errors.root && (
                <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">
                  {errors.root.message}
                </div>
              )}

              <div>
                <label htmlFor="email" className="label">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  className="input"
                  {...register('email', { required: 'Email is required' })}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="label">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  className="input"
                  {...register('password', { required: 'Password is required' })}
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                disabled={loginMutation.isPending}
                className="btn-primary w-full"
              >
                {loginMutation.isPending ? 'Signing in...' : 'Sign in'}
              </button>
            </form>
          )}

          <div className="mt-6 text-center text-sm text-gray-600">
            Don't have an account?{' '}
            <Link to="/register" className="text-primary-600 hover:text-primary-700 font-medium">
              Create one
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
